/**
 * Agent Analysis Service
 * Handles disinformation analysis with progressive updates
 */

import { httpClient, ApiError } from './http.client';
import { AGENT_ENDPOINTS } from './api.constants';

// =============================================================================
// Types
// =============================================================================

export type ProgressStepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

export interface ProgressStep {
  id: string;
  name: string;
  nameRo: string;
  status: ProgressStepStatus;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  count?: number;
  error?: string;
}

export interface DetectedTechnique {
  technique_id: number;
  technique_name: string;
  dimension_code: string;
  subdimension_name: string;
  severity: number;
  confidence: number;
  intensity: number;
  evidence: string;
  indicators_matched: string[];
}

export interface VerifiedClaim {
  claim_index: number;
  text: string;
  is_checkable: boolean;
  claim_type: string;
  context: string;
  status_code: string;
  status_name: string;
  status_color: string;
  confidence: number;
  evidence: string;
  sources: Array<{ url: string; title: string; type: string }>;
}

export interface AssessedSource {
  url: string;
  domain: string;
  source_type: string;
  base_score: number;
  final_score: number;
  red_flags: Array<{ flag: string; severity: number; action: string }>;
}

export interface DimensionResult {
  dimension_code: string;
  dimension_name: string;
  weight: number;
  score: number;
  techniques: DetectedTechnique[];
  notes: string;
}

export interface FinalVerdict {
  score: number;
  score_raw: number;
  verdict_code: string;
  verdict_name: string;
  verdict_color: string;
  confidence: {
    level: string;
    score: number;
    color: string;
  };
  risk: {
    level: number;
    name: string;
    color: string;
  };
  components: {
    manipulation: number;
    claims: number;
    source: number;
    ai: number;
    context: number;
  };
  weights: Record<string, number>;
}

export interface AnalysisProgress {
  sessionId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  currentStep: string | null;
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
  totalDurationMs?: number;
  steps: ProgressStep[];
  partialResults: {
    techniques?: DetectedTechnique[];
    claims?: VerifiedClaim[];
    sources?: AssessedSource[];
    dimensions?: DimensionResult[];
    verdict?: FinalVerdict;
    score?: number;
  };
  error?: string;
}

export interface FullAnalysis {
  session_id: string;
  status: string;
  created_at: string;
  completed_at?: string;
  processing_time_ms?: number;
  input: {
    content: string;
    type: string;
  };
  content_type?: string;
  dimensions: DimensionResult[];
  techniques: DetectedTechnique[];
  extracted_claims: Array<{
    claim_index: number;
    text: string;
    is_checkable: boolean;
    claim_type: string;
    context: string;
  }>;
  verified_claims: VerifiedClaim[];
  sources: AssessedSource[];
  multipliers: {
    topic?: { name: string; multiplier: number; reason: string };
    temporal?: { name: string; multiplier: number; reason: string } | null;
    reach?: { name: string; multiplier: number; estimated_reach: string };
    combined_multiplier: number;
  };
  verdict: FinalVerdict;
  steps: Array<{
    name: string;
    status: string;
    duration_ms?: number;
    error?: string;
  }>;
  model?: string;
  error?: string;
}

export interface UIMapping {
  verdict: {
    category: string;
    score: number;
    color: string;
    confidence: number;
    confidence_label: string;
    description: string;
  };
  classification: {
    primary_category: string;
    content_type: string;
    dimensions: Array<{
      code: string;
      name: string;
      score: number;
      weight: number;
      techniques_count: number;
    }>;
  };
  techniques: Array<{
    name: string;
    dimension: string;
    severity: number;
    confidence: number;
    evidence: string;
  }>;
  claims: Array<{
    text: string;
    status: string;
    status_code: string;
    color: string;
    evidence: string;
  }>;
  sources: {
    total: number;
    credible: number;
    questionable: number;
    risk_level: string;
    items: Array<{
      domain: string;
      type: string;
      score: number;
      red_flags: string[];
    }>;
  };
  recommendations: Array<{
    priority: string;
    action: string;
    rationale: string;
  }>;
}

export interface AnalysisResponse {
  success: boolean;
  sessionId: string;
  pipeline: string;
  analysis?: FullAnalysis;
  uiMapping?: UIMapping;
  summary?: string;
  error?: string;
  meta?: {
    durationMs: number;
    model?: string;
    paramsUsed?: boolean;
    paramsLoaded?: boolean;
    paramsStats?: Record<string, number>;
  };
}

export interface AnalyzeRequest {
  sessionId?: string;
  prompt: string;
  pipeline?: string;
  model?: string;
}

// =============================================================================
// Service Class
// =============================================================================

class AgentService {
  /**
   * Generate a unique session ID
   */
  generateSessionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `web-${timestamp}-${random}`;
  }

  /**
   * Start a full analysis
   */
  async analyzeText(
    prompt: string,
    options?: {
      sessionId?: string;
      pipeline?: string;
      model?: string;
    }
  ): Promise<AnalysisResponse> {
    const sessionId = options?.sessionId || this.generateSessionId();

    const request: AnalyzeRequest = {
      sessionId,
      prompt,
      pipeline: options?.pipeline || 'disinformation',
      model: options?.model,
    };

    const response = await httpClient.post<AnalysisResponse>(
      AGENT_ENDPOINTS.ANALYZE_FULL,
      request
    );

    return response;
  }

  /**
   * Get analysis progress (for polling)
   */
  async getProgress(sessionId: string): Promise<AnalysisProgress | null> {
    try {
      const response = await httpClient.get<AnalysisProgress>(
        AGENT_ENDPOINTS.PROGRESS(sessionId)
      );
      return response;
    } catch (error) {
      // 404 means analysis not found yet
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get full analysis result
   */
  async getAnalysis(sessionId: string): Promise<{
    sessionId: string;
    status: string;
    analysis?: FullAnalysis;
    uiMapping?: UIMapping;
    progress?: AnalysisProgress;
  } | null> {
    try {
      const response = await httpClient.get<{
        sessionId: string;
        status: string;
        analysis?: FullAnalysis;
        uiMapping?: UIMapping;
        progress?: AnalysisProgress;
      }>(AGENT_ENDPOINTS.ANALYSIS(sessionId));
      return response;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Analyze with progress polling
   * Returns an async generator that yields progress updates
   */
  async *analyzeWithProgress(
    prompt: string,
    options?: {
      sessionId?: string;
      pipeline?: string;
      pollInterval?: number;
      maxWaitTime?: number;
    }
  ): AsyncGenerator<AnalysisProgress | AnalysisResponse, AnalysisResponse, unknown> {
    const sessionId = options?.sessionId || this.generateSessionId();
    const pollInterval = options?.pollInterval || 2000;
    const maxWaitTime = options?.maxWaitTime || 300000; // 5 minutes

    // Start analysis (non-blocking conceptually, but we wait for initial response)
    // The actual analysis runs in background on the server
    const startTime = Date.now();

    // Trigger analysis — starts processing on server
    const analysisPromise = this.analyzeText(prompt, {
      sessionId,
      pipeline: options?.pipeline,
    });

    // Wait one poll interval before first poll so the server has time to create the session
    await new Promise(resolve => setTimeout(resolve, pollInterval));

    // Poll for progress until complete
    while (Date.now() - startTime < maxWaitTime) {
      const progress = await this.getProgress(sessionId);
      if (progress) {
        yield progress;

        if (progress.status === 'completed' || progress.status === 'failed') {
          break;
        }
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    // Wait for the analysis to complete and return final result
    const result = await analysisPromise;
    return result;
  }

  /**
   * Check service health
   */
  async checkHealth(): Promise<{ status: string; version: string; uptime: number }> {
    return httpClient.get(AGENT_ENDPOINTS.HEALTH);
  }

  /**
   * Get service status with detailed info
   */
  async getStatus(): Promise<{
    status: string;
    version: string;
    uptime: number;
    providers: { anthropic: boolean; openai: boolean };
    pipelines: string[];
    params?: {
      loaded: boolean;
      loadedAt?: string;
      stats?: Record<string, number>;
    };
  }> {
    return httpClient.get(AGENT_ENDPOINTS.STATUS);
  }
}

export const agentService = new AgentService();
